import { NzMessageService } from 'ng-zorro-antd/message';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpAgentService } from '../../service/http-agent.service';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, of, Subscription, switchMap, tap } from 'rxjs';
import { AgentDto, AgentProvisionFormDataDto, AppRoleDto, GovernmentRecordDto, GovernmentRecordLookupDto, PartnerDto } from '../../service/dto';


export function uniqueFieldValidator(agentService: HttpAgentService, field: string, agent?: AgentDto): AsyncValidatorFn {
	return (control: AbstractControl) => {
		if (!control.value) {
			return of(null);
		}
		return of(control.value).pipe(
			debounceTime(500),
			distinctUntilChanged(),
			switchMap(value =>
				agentService.validate(field, value, agent).pipe(
					map(result => {
						if (result.valid == true) {
							return null;
						}
						if (result.reason == 'NotUnique') {
							return { notUnique: true };
						}
						if (result.reason == 'InvalidMsisdn') {
							return { invalidMsisdn: true };
						}
						if (result.reason == 'InvalidDomain') {
							return { invalidDomain: true };
						}
						return { invalid: true };
					}),
					catchError(() => of(null))
				)
			)
		);
	};
}
// export function doesDocumentExist(agentService: HttpAgentService, instance: AgentProvisionComponent): AsyncValidatorFn {
// 	return (control: AbstractControl) => {
// 		if (!control.value) {
// 			instance.governmentRecordResult = undefined;
// 			return of(null);
// 		}
// 		const documentNumber = instance.documentNumber;
// 		if (!documentNumber || documentNumber.trim().length <= 3) {
// 			instance.governmentRecordResult = undefined;
// 			return of(null);
// 		}
// 		return of(control.value).pipe(
// 			debounceTime(500),
// 			distinctUntilChanged(),
// 			switchMap(value =>
// 				agentService.getGovernmentRecord(instance.documentNumber).pipe(
// 					map(result => {
// 						instance.governmentRecordResult = result;

// 						if(result.duplicate == true){
// 							return { idAlreadyUsed: true };
// 						}
// 						if(result.governmentRecord == undefined || result.governmentRecord == null){
// 							return { notFound: true };
// 						}

// 						return null;
// 					}),
// 					catchError(() => of(null))
// 				)
// 			)
// 		);
// 	};
// }

@Component({
	selector: 'app-agent-provision',
	standalone: false,
	templateUrl: './agent-provision.component.html',
	styleUrl: './agent-provision.component.scss'
})
export class AgentProvisionComponent implements OnInit, OnDestroy {
	agentForm!: FormGroup;
	data?: AgentProvisionFormDataDto;
	governmentRecordResult?: GovernmentRecordLookupDto;
	subscription = new Subscription();
	loading = false;
	saving = false;

	constructor(private agentService: HttpAgentService, private fb: FormBuilder, private messageService: NzMessageService) { }

	ngOnInit(): void {
		this.agentService.getProvisionData().subscribe(result => {
			this.data = result;
		});

		this.agentForm = this.fb.group({
			username: ['', [Validators.required, Validators.pattern('^[a-zA-Z][a-zA-Z0-9_-]+$')], [uniqueFieldValidator(this.agentService, 'username')]],
			email: ['', [Validators.required, Validators.email], [uniqueFieldValidator(this.agentService, 'email')]],
			msisdn: ['', [Validators.required, Validators.pattern(/^\d{10,15}$/)], [uniqueFieldValidator(this.agentService, 'msisdn')]],
			partner: ['', Validators.required],
			role: ['', Validators.required],
			idNumber: ['', [Validators.required, Validators.minLength(8)]]
		});

		// this.agentForm.get('idNumber')!.valueChanges.pipe(
		// 	debounceTime(500),
		// 	distinctUntilChanged(),
		//Filter returns true if length is 8+ characters
		//call agentService.getGovernmentRecord(value) -- accepts string
		//set this.governmentRecordResult with result
		//create new governmentRecordResult with errorMessage containing the error message.

		this.subscription.add(this.agentForm.get('idNumber')!.valueChanges.pipe(
			debounceTime(750),
			distinctUntilChanged(),
			filter(value => !!value && value.length >= 8), // Only proceed if input has 8+ characters
			tap(value => this.governmentRecordResult = undefined),
			switchMap(value => {
				this.loading = true;
				return this.agentService.getGovernmentRecord(value).pipe(
					catchError(error => {
						const fallback: GovernmentRecordLookupDto = {
							hashCode: 0,
							duplicate: false,
							errorMessage: error?.error?.message || 'Failed to fetch government record'
						};
						return of(fallback);
					}),
					finalize(() => {
						this.loading = false; // ✅ always runs: success, error, or cancel
					})
				)
			})
		).subscribe(result => {
			this.governmentRecordResult = result;
		}));
	}
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	submit(): void {
		if (this.agentForm.valid) {
			const agentData = this.agentForm.value;
			console.log('Provision agent:', agentData);
			const payload: AgentDto = {
				id: 0,
				createdAt: new Date(),
				status: 'PendingRegistration',
				username: this.agentForm.get('username')!.value,
				emailAddress: this.agentForm.get('email')!.value,
				msisdn: this.agentForm.get('msisdn')!.value,
				partner: this.agentForm.get('partner')!.value,
				appRole: this.agentForm.get('role')!.value,
				governmentRecord: {
					id: 0,
					createdAt: new Date(),
					idNumber: this.agentForm.get('idNumber')!.value,
					source: 'Manual',
					lookupTime: 0,
					searchType: '-',
					searchKey: '-',
					documentType: 'Passport',
					failed: false,
					hashCode: 0
				},
				hashCode: 0
			};
			const that = this;
			this.agentService.provision(payload).subscribe(result => {
				this.agentForm.reset();
				this.messageService.info('Agent successfully provisioned');
			});
		} else {
			this.agentForm.markAllAsTouched();
		}
	}

	controlInvalid(name: string): boolean {
		const ctrl = this.agentForm.get(name);
		return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
	}

	getError(name: string): string | null {
		const ctrl = this.agentForm.get(name);
		if (!ctrl || !ctrl.errors) return null;

		if (ctrl.errors['required']) return 'This field is required';
		if (ctrl.errors['email']) return 'Invalid email format';
		if (ctrl.errors['pattern']) return 'Invalid format';
		if (ctrl.errors['notUnique']) return 'Already in use';
		if (ctrl.errors['invalidDomain']) return 'Invalid domain';
		if (ctrl.errors['InvalidMsisdn']) return 'Invalid format';
		if (ctrl.errors['invalid']) return 'Invalid';
		if (ctrl.errors['notFound']) return 'Document not found';
		if (ctrl.errors['idAlreadyUsed']) return 'Already Used by another Agent';

		return 'Invalid value';
	}
	getErrors(name: string) {
		return this.agentForm.get(name)?.errors;
	}




	get isIdNumberInvalid(): boolean {
		if (this.controlInvalid("idNumber")) {
			return true;
		}
		if (!this.governmentRecordResult) {
			return true;
		}
		if (this.governmentRecordResult.errorMessage) {
			return true;
		}
		if (this.governmentRecordResult.duplicate) {
			return true;
		}
		if (!this.governmentRecordResult.governmentRecord) {
			return true;
		}
		if (this.governmentRecordResult.governmentRecord.failed) {
			return true;
		}
		return false;
	}
	get idNumberError(): string | null {
		if (this.controlInvalid("idNumber")) {
			return this.getError('idNumber');
		}
		const ctrl = this.agentForm.get('idNumber')!;
		if(ctrl.touched == false){
			return null;
		}

		if (!this.governmentRecordResult) {
			return 'Document not found';
		}
		if (this.governmentRecordResult.errorMessage) {
			return this.governmentRecordResult.errorMessage;
		}
		if (this.governmentRecordResult.duplicate) {
			return 'Already Used by another Agent';
		}
		if (!this.governmentRecordResult.governmentRecord) {
			return 'Document not found';
		}
		if (this.governmentRecordResult.governmentRecord.failed) {
			return this.governmentRecordResult.governmentRecord.failedReason ?? 'Failed to load government record';
		}
		return null;
	}

	isFormInvalid(): boolean {
		if (!this.agentForm.valid) {
			return true;
		}
		return this.isIdNumberInvalid;
	}
	// getFormValidationErrors(form: FormGroup): any[] {
	// 	const result: any[] = [];

	// 	Object.keys(form.controls).forEach(key => {
	// 		const control = form.get(key);

	// 		if (control instanceof FormGroup) {
	// 			result.push(...this.getFormValidationErrors(control));
	// 		} else if (control && control.errors) {
	// 			result.push({ field: key, errors: control.errors });
	// 		}
	// 	});

	// 	return result;
	// }
	// get errorsList() {
	// 	return this.getFormValidationErrors(this.agentForm);
	// }
}
